import { SvgIcon } from '@screentone/core';

import { getContentTypeIcons } from 'components/content-type-token/ContentTypeToken';
import { PublicationSettingSearchableContentType } from 'data/generated/graphql';
import { AllessehContent } from 'hooks/useAllessehContentQuery';
import { isJWSJOpinion } from 'utils/modules';

export const isCwsjVideo = (content: AllessehContent) =>
  content.data.attributes.product === 'WSJ Video' && content.data.attributes.language_code === 'zh-cn';

export const isOpinion = (content: AllessehContent) =>
  content.data.attributes.section_name === 'Opinion' ||
  content.data.attributes.section_name === 'CN-Opinion' ||
  content.data.attributes.product === 'WSJ Opinion Video' ||
  isJWSJOpinion(content.data.attributes.section_type);

const getType = (content: AllessehContent): PublicationSettingSearchableContentType | string => {
  if (content.data.attributes.product.includes('Live Coverage')) {
    return PublicationSettingSearchableContentType.LiveCoverage.toLowerCase();
  }
  if (content.data.attributes.product === 'Interactive Media') {
    return PublicationSettingSearchableContentType.Tappable.toLowerCase();
  }

  if (
    content.data.attributes.product.startsWith('Oxford Analytica') &&
    content.data.attributes.do_not_archive === true
  ) {
    return PublicationSettingSearchableContentType.OxanAnnouncements.toLowerCase();
  }

  return content.data.attributes.type;
};

const getBrandType = (content: AllessehContent, publication: string): string | null => {
  if (isOpinion(content)) {
    if (publication !== 'wsjopinion') {
      return PublicationSettingSearchableContentType.Opinion.toLowerCase();
    }
    return null;
  }

  switch (content.data.attributes.product) {
    case 'MarketWatch.com':
      if (publication !== 'marketwatch') {
        return PublicationSettingSearchableContentType.Marketwatch.toLowerCase();
      }
      return null;

    case 'Free Expression':
      return PublicationSettingSearchableContentType.FreeExpression.toLowerCase();

    case 'Barrons Investor Circle':
    case 'Barrons Blogs':
    case 'Barrons.com':
      if (publication !== 'barrons') {
        return PublicationSettingSearchableContentType.Barrons.toLowerCase();
      }
      return null;

    case 'Mansion Global':
      if (publication !== 'mansionglobal') {
        return PublicationSettingSearchableContentType.MansionGlobal.toLowerCase();
      }
      return null;

    case 'Agence France Presse':
      return PublicationSettingSearchableContentType.Afp.toLowerCase();

    case 'WSJ.com':
      if (publication !== 'wsj') {
        return PublicationSettingSearchableContentType.Wsj.toLowerCase();
      }
      return null;

    case 'WSJPRO':
      return PublicationSettingSearchableContentType.WsjPro.toLowerCase();

    case 'Private Market Events':
      if (publication !== 'privatemarket') {
        return PublicationSettingSearchableContentType.PrivateMarketEvents.toLowerCase();
      }
      return null;

    case 'IBD':
    case 'Investor Business Daily':
      if (publication !== 'ibd') {
        return PublicationSettingSearchableContentType.Ibd.toLowerCase();
      }
      return null;

    case 'PENEWS':
      if (publication !== 'pen') {
        return PublicationSettingSearchableContentType.Penews.toLowerCase();
      }
      return null;

    case 'FN Online':
      if (publication !== 'fnlondon') {
        return PublicationSettingSearchableContentType.FnOnline.toLowerCase();
      }
      return null;

    case 'Oxford Analytica':
      if (publication !== 'oxan') {
        return PublicationSettingSearchableContentType.OxfordAnalytica.toLowerCase();
      }
      return null;

    default:
      return null;
  }
};

const makeCustomSvgIcon = (svgPath: string): React.ComponentType<React.ComponentProps<typeof SvgIcon>> =>
  function CustomSvgIcon(props: React.ComponentProps<typeof SvgIcon>) {
    return (
      <SvgIcon {...props} viewBox="0 0 48 48">
        <path d={svgPath} />
      </SvgIcon>
    );
  };

export const getTypeAndBrandIcons = (
  allessehContent: AllessehContent,
  currentProperty: string | null,
  iconSvgOverrides?: Record<string, string>
): {
  typeIcons: ReturnType<typeof getContentTypeIcons>;
  brandIcon: ReturnType<typeof getContentTypeIcons>[0] | null;
} => {
  const type = getType(allessehContent);
  const mappedBrand = getBrandType(allessehContent, currentProperty ?? '');

  const customTypePath = iconSvgOverrides?.[type];
  const typeIcons = customTypePath
    ? [{ icon: makeCustomSvgIcon(customTypePath) as React.ComponentType<unknown>, description: type }]
    : getContentTypeIcons(type, allessehContent);

  const { product } = allessehContent.data.attributes;
  const customBrandPath = iconSvgOverrides?.[product] ?? iconSvgOverrides?.[product.toLowerCase()];

  let brandIcon: ReturnType<typeof getContentTypeIcons>[0] | null = null;
  if (customBrandPath) {
    brandIcon = { icon: makeCustomSvgIcon(customBrandPath) as React.ComponentType<unknown>, description: product };
  } else if (mappedBrand) {
    brandIcon = getContentTypeIcons(mappedBrand, allessehContent)[0] ?? null;
  }

  return { typeIcons, brandIcon };
};
